import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { ChevronRight, Star, Play, Dumbbell, Users, Award, Zap, Check, ShoppingBag, Calendar, Ticket, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Logo from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(target);
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);

  return (
    <span ref={ref} className="stat-number">
      {count}<span className="text-3xl sm:text-4xl md:text-6xl">{suffix}</span>
    </span>
  );
};

const AnimatedLine = ({ delay = 0 }) => (
  <motion.div
    className="h-px bg-gradient-to-r from-transparent via-uf-red to-transparent"
    initial={{ scaleX: 0, opacity: 0 }}
    whileInView={{ scaleX: 1, opacity: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1, delay }}
  />
);

const HeroSection = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-uf-black" data-testid="hero-section">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-uf-black via-[#0d0d0d] to-uf-black" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-uf-red/5 rounded-full blur-[150px]" />
      </div>
      
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-8 left-8 w-20 h-20 border-l-2 border-t-2 border-uf-red/20" />
        <div className="absolute bottom-8 right-8 w-20 h-20 border-r-2 border-b-2 border-uf-red/20" />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 border border-uf-red/5 animate-[spin_60s_linear_infinite]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
      </div>

      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />
      
      <motion.div 
        className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto pt-24 md:pt-20"
        style={{ opacity }}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ delay: 0.2, type: "spring", stiffness: 100 }} 
          className="mb-6 md:mb-8"
        >
          <Logo size="lg" className="mx-auto" />
        </motion.div>
        
        <div className="overflow-hidden">
          <motion.h1 
            className="font-heading text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-bold text-white hero-text mb-4 md:mb-6" 
            initial={{ y: 100, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }} 
            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }} 
            data-testid="hero-title"
          >
            BUILD YOUR<br/>
            <motion.span 
              className="text-uf-red inline-block relative"
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(227, 24, 55, 0.5)",
                  "0 0 60px rgba(227, 24, 55, 0.8)",
                  "0 0 20px rgba(227, 24, 55, 0.5)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              LEGACY
              <motion.div 
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-uf-red to-transparent"
                animate={{ scaleX: [0.5, 1, 0.5], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
            </motion.span>
          </motion.h1>
        </div>
        
        <motion.p 
          className="text-base sm:text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8 md:mb-10 font-normal px-4" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 0.5 }}
        >
          Vision without action is just a dream. Take the first step and join the elite.
        </motion.p>
        
        <motion.div 
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4" 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.6 }}
        >
          <Link to="/membership" className="btn-primary" data-testid="hero-cta"><span>Sign Up Now</span></Link>
          <Link to="/book-tour" className="btn-secondary">Book a Tour</Link>
        </motion.div>
        
        <motion.div 
          className="flex justify-center gap-8 md:gap-16 mt-12 md:mt-16 pt-8 border-t border-white/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {[
            { value: '11+', label: 'Locations' },
            { value: '15K', label: 'Sq Ft Avg' },
            { value: '24/7', label: 'Support' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-heading text-2xl md:text-3xl font-bold text-uf-red">{stat.value}</p>
              <p className="text-white/50 text-xs uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10" 
        animate={{ y: [0, 10, 0] }} 
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <div className="flex flex-col items-center gap-1">
          <ChevronRight className="rotate-90 text-uf-red/50" size={20} />
        </div>
      </motion.div>
    </section>
  );
};

const StatsSection = () => {
  const stats = [
    { number: '11', label: 'Locations', suffix: '+' },
    { number: '15', label: 'Sq Ft Avg', suffix: 'K' },
    { number: '500', label: 'Members', suffix: '+' },
    { number: '10', label: 'Years Strong', suffix: '+' },
  ];

  return (
    <section className="py-16 md:py-20 bg-uf-surface border-y border-uf-border relative overflow-hidden" data-testid="stats-section">
      <div className="absolute inset-0 opacity-30">
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-uf-red/10 via-transparent to-uf-red/10"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        />
      </div>
      
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
        <AnimatedLine delay={0.2} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 py-8 md:py-12">
          {stats.map((stat, i) => (
            <motion.div 
              key={stat.label} 
              className="text-center"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, type: "spring", stiffness: 100 }}
            >
              <AnimatedCounter target={stat.number} suffix={stat.suffix} duration={1.5} />
              <motion.p 
                className="text-uf-textMuted uppercase tracking-widest text-xs md:text-sm mt-2"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 + 0.5 }}
              >
                {stat.label}
              </motion.p>
            </motion.div>
          ))}
        </div>
        <AnimatedLine delay={0.4} />
      </div>
    </section>
  );
};

const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <section className="py-24 md:py-32" data-testid="video-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-uf-red font-heading text-xl tracking-[0.3em] mb-2">EXPLORE</p>
          <h2 className="font-heading text-5xl md:text-7xl font-bold text-white">What is UltraFlex?</h2>
        </motion.div>
        <motion.div className="relative aspect-video bg-uf-surface border border-uf-border overflow-hidden" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
          {!isPlaying ? (
            <div className="absolute inset-0 flex items-center justify-center cursor-pointer group" onClick={() => setIsPlaying(true)}>
              <img src="https://img.youtube.com/vi/CyWN8ipcwgo/maxresdefault.jpg" alt="UltraFlex Video" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>
              <div className="relative z-10 w-20 h-20 md:w-24 md:h-24 bg-uf-red flex items-center justify-center group-hover:scale-110 transition-transform" data-testid="play-video-btn">
                <Play className="text-white ml-1" size={32} fill="white" />
              </div>
            </div>
          ) : (
            <iframe src="https://www.youtube.com/embed/CyWN8ipcwgo?autoplay=1" title="UltraFlex Gym Tour" className="absolute inset-0 w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    { icon: Dumbbell, title: 'Premium Equipment', desc: 'Hand-picked from top manufacturers worldwide, custom-made for maximum results.' },
    { icon: Users, title: 'Expert Trainers', desc: 'Professional bodybuilders and certified PTs ready to help you achieve your goals.' },
    { icon: Award, title: 'No Waiting', desc: 'Multiple racks and machines for every body part. Train when you want.' },
    { icon: Zap, title: 'Flexible Hours', desc: 'Extended opening hours across all locations. Train when it suits you.' },
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-uf-surface relative overflow-hidden" data-testid="features-section">
      <div className="absolute inset-0 pointer-events-none">
        <motion.div className="absolute top-20 left-10 w-32 h-32 border border-uf-red/10 rotate-45" animate={{ rotate: [45, 90, 45], scale: [1, 1.1, 1] }} transition={{ duration: 20, repeat: Infinity }} />
        <motion.div className="absolute bottom-20 right-10 w-24 h-24 border border-uf-red/10" animate={{ rotate: [0, -45, 0], scale: [1, 1.2, 1] }} transition={{ duration: 15, repeat: Infinity }} />
      </div>
      
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
        <motion.div className="text-center mb-12 md:mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-uf-red font-heading text-lg md:text-xl tracking-[0.3em] mb-2">WHY US</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-7xl font-bold text-white">Not Your Average Gym</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((feature, index) => (
            <motion.div 
              key={feature.title} 
              className="group bg-uf-black border border-uf-border p-6 md:p-8 relative overflow-hidden" 
              initial={{ opacity: 0, y: 30 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5, borderColor: "rgba(227, 24, 55, 0.5)" }}
            >
              <motion.div className="absolute inset-0 bg-gradient-to-br from-uf-red/0 to-uf-red/0 group-hover:from-uf-red/5 group-hover:to-transparent transition-all duration-500" />
              <motion.div className="feature-icon relative z-10" whileHover={{ scale: 1.1, rotate: 5 }} transition={{ type: "spring", stiffness: 300 }}>
                <feature.icon size={24} />
              </motion.div>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 md:mb-3 relative z-10">{feature.title}</h3>
              <p className="text-uf-textMuted text-xs md:text-sm leading-relaxed relative z-10">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { axios.get(`${API}/reviews`).then(res => setReviews(res.data)).catch(() => {}); }, []);

  return (
    <section className="py-24 md:py-32" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <p className="text-uf-red font-heading text-xl tracking-[0.3em] mb-2">TESTIMONIALS</p>
          <h2 className="font-heading text-5xl md:text-7xl font-bold text-white">What Members Say</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((review, index) => (
            <motion.div key={review.id} className="review-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} data-testid={`review-card-${review.id}`}>
              <div className="flex items-center gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} size={16} className="text-uf-red fill-uf-red" />)}</div>
              <p className="text-uf-text text-sm leading-relaxed mb-6">"{review.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-uf-red/20 flex items-center justify-center font-heading text-uf-red text-xl">{review.name.charAt(0)}</div>
                <div><p className="text-white font-medium text-sm">{review.name}</p><p className="text-uf-textMuted text-xs">Facebook Review</p></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => (
  <section className="py-16 md:py-24 lg:py-32 bg-uf-surface" data-testid="about-section">
    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <p className="text-uf-red font-heading text-lg md:text-xl tracking-[0.3em] mb-2">ABOUT US</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">Welcome To UltraFlex</h2>
          <div className="space-y-3 md:space-y-4 text-uf-textMuted leading-relaxed text-sm md:text-base">
            <p>Each UltraFlex is under development for several months. We don't do average - we're building a legacy, competing alongside some of the top gyms in the world.</p>
            <p>We choose the hard route: 100% no holds barred fit out over 10-15,000 sq.ft, with brand new changing facilities, studio & treatment rooms and even our own barbershop.</p>
            <p>We then hand-pick the best equipment from the top independent manufacturers around the world, some of which are custom made, to ensure our members have exactly what they need to achieve their goals.</p>
            <p>Whether you are an experienced strength athlete, or just starting out and want to lose weight or get in shape, we have everything you need. We are open to all, with a friendly and positive environment to make you feel welcome.</p>
          </div>
          <Link to="/locations" className="btn-primary mt-6 md:mt-8 inline-block" data-testid="about-cta"><span>Explore Locations</span></Link>
        </motion.div>
        <motion.div className="relative" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <img src="https://ultraflexgym.co.uk/wp-content/uploads/2019/06/IMG-111-1024x683.jpg" alt="UltraFlex Interior" className="w-full aspect-[4/3] object-cover" />
          <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-uf-red p-4 md:p-6 lg:p-8">
            <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">10+</p>
            <p className="text-white/80 text-xs md:text-sm uppercase tracking-wider">Years of Excellence</p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const CTABannersSection = () => {
  const banners = [
    { title: 'Ultra Flex Store', subtitle: 'Shop merch & supplements', link: '/shop', image: 'https://ultraflexgym.co.uk/wp-content/uploads/2022/05/UltraFlex-Clothing.jpg', icon: ShoppingBag },
    { title: 'Day Pass', subtitle: 'Try us out for a day - £10', link: '/day-pass', image: 'https://ultraflexgym.co.uk/wp-content/uploads/2019/10/IMG-75-1024x683.jpg', icon: Ticket },
    { title: 'Book a Tour', subtitle: 'See our facilities', link: '/book-tour', image: 'https://ultraflexgym.co.uk/wp-content/uploads/2019/03/UFG-64-1024x615.jpg', icon: Calendar },
    { title: 'Find a Location', subtitle: '11 gyms across UK & Greece', link: '/locations', image: 'https://ultraflexgym.co.uk/wp-content/uploads/2022/03/UFG-1.jpg', icon: MapPin },
  ];

  return (
    <section className="py-8 md:py-12" data-testid="cta-banners">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {banners.map((banner) => (
            <Link to={banner.link} key={banner.title} className="group relative h-48 sm:h-56 md:h-64 overflow-hidden">
              <img src={banner.image} alt={banner.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-end p-4 md:p-6 text-center">
                <banner.icon className="text-uf-red mb-2" size={28} />
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white">{banner.title}</h3>
                <p className="text-uf-textMuted text-sm">{banner.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const LocationsShowcase = () => {
  const [activeLocation, setActiveLocation] = useState(0);

  const locationData = [
    { id: "west-leeds", name: "West Leeds", city: "Leeds", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/06/IMG-111-1024x683.jpg" },
    { id: "north-leeds", name: "North Leeds", city: "Leeds", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/03/UFG-64-1024x615.jpg" },
    { id: "york", name: "York", city: "York", image: "https://ultraflexgym.co.uk/wp-content/uploads/2022/03/UFG-1.jpg" },
    { id: "hull", name: "Hull", city: "Hull", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/10/IMG-75-1024x683.jpg" },
    { id: "durham", name: "Durham", city: "Durham", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/06/IMG-111-1024x683.jpg" },
    { id: "derby", name: "Derby", city: "Derby", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/03/UFG-64-1024x615.jpg" },
    { id: "rotherham", name: "Rotherham", city: "Rotherham", image: "https://ultraflexgym.co.uk/wp-content/uploads/2022/03/UFG-1.jpg" },
    { id: "lincoln", name: "Lincoln", city: "Lincoln", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/10/IMG-75-1024x683.jpg" },
    { id: "west-london", name: "West London", city: "London", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/06/IMG-111-1024x683.jpg" },
    { id: "athens", name: "Athens", city: "Greece", image: "https://ultraflexgym.co.uk/wp-content/uploads/2019/03/UFG-64-1024x615.jpg" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLocation((prev) => (prev + 1) % locationData.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [locationData.length]);

  return (
    <section className="py-16 md:py-24 relative overflow-hidden" data-testid="locations-showcase">
      <div className="absolute inset-0">
        {locationData.map((loc, index) => (
          <motion.div key={loc.id} className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: index === activeLocation ? 0.15 : 0 }} transition={{ duration: 1 }}>
            <img src={loc.image} alt="" className="w-full h-full object-cover" />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-uf-black via-uf-black/95 to-uf-black"></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-uf-red font-heading text-lg md:text-xl tracking-[0.3em] mb-2">FIND YOUR GYM</p>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                11 Locations<br/><span className="text-uf-red">Across UK & Greece</span>
              </h2>
              <p className="text-white/80 mb-8 text-sm md:text-base">Premium facilities ranging from 10,000-16,000 sq ft, packed with top-tier equipment and expert trainers.</p>
            </motion.div>

            <div className="flex flex-wrap gap-2 md:gap-3">
              {locationData.map((loc, index) => (
                <motion.button
                  key={loc.id}
                  onClick={() => setActiveLocation(index)}
                  className={`px-4 py-2 md:px-5 md:py-2.5 font-heading text-sm md:text-base uppercase tracking-wider transition-all duration-300 ${
                    index === activeLocation 
                      ? 'bg-uf-red text-white shadow-lg shadow-uf-red/30' 
                      : 'bg-uf-surface/80 text-white/70 hover:text-white hover:bg-uf-surfaceHi border border-uf-border'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {loc.name}
                </motion.button>
              ))}
            </div>

            <Link to="/locations" className="inline-flex items-center gap-2 mt-8 text-uf-red font-heading text-lg uppercase tracking-wider hover:gap-4 transition-all">
              View All Locations <ArrowRight size={20} />
            </Link>
          </div>

          <motion.div className="relative" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <AnimatePresence mode="wait">
              <motion.div key={activeLocation} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }} className="relative">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={locationData[activeLocation].image} alt={locationData[activeLocation].name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-uf-black via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 text-uf-red mb-2">
                      <MapPin size={18} />
                      <span className="text-sm uppercase tracking-wider">{locationData[activeLocation].city}</span>
                    </div>
                    <h3 className="font-heading text-3xl md:text-4xl font-bold text-white">UltraFlex {locationData[activeLocation].name}</h3>
                  </div>
                </div>
                <div className="grid grid-cols-3 bg-uf-surface border border-uf-border">
                  <div className="p-4 text-center border-r border-uf-border"><p className="font-heading text-2xl md:text-3xl font-bold text-uf-red">15K</p><p className="text-xs text-white/60 uppercase tracking-wider">Sq Ft</p></div>
                  <div className="p-4 text-center border-r border-uf-border"><p className="font-heading text-2xl md:text-3xl font-bold text-uf-red">Free</p><p className="text-xs text-white/60 uppercase tracking-wider">Parking</p></div>
                  <div className="p-4 text-center"><p className="font-heading text-2xl md:text-3xl font-bold text-uf-red">Pro</p><p className="text-xs text-white/60 uppercase tracking-wider">Trainers</p></div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="mt-4 flex gap-1">
              {locationData.map((_, index) => (
                <motion.div key={index} className="h-1 flex-1 bg-uf-border overflow-hidden">
                  <motion.div className="h-full bg-uf-red" initial={{ width: "0%" }} animate={{ width: index === activeLocation ? "100%" : index < activeLocation ? "100%" : "0%" }} transition={{ duration: index === activeLocation ? 3 : 0.3, ease: "linear" }} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cta-section">
    <div className="absolute inset-0">
      <img src="https://ultraflexgym.co.uk/wp-content/uploads/2019/10/IMG-75-1024x683.jpg" alt="Gym Background" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-uf-black/85"></div>
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 text-center">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <p className="text-uf-red font-heading text-lg md:text-xl tracking-[0.3em] mb-3 md:mb-4">READY TO START?</p>
        <h2 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 md:mb-6">Set Up Easy & Affordable<br/>Monthly Payments</h2>
        <p className="text-base md:text-xl text-uf-textMuted max-w-2xl mx-auto mb-8 md:mb-10 px-4">Take the first step because vision without action is just a dream.</p>
        <Link to="/membership" className="btn-primary" data-testid="cta-signup"><span>Sign Up Now</span></Link>
        <p className="text-uf-textMuted text-xs md:text-sm mt-4 md:mt-6 px-4">Existing membership enquiries: <a href="mailto:memberships@ultraflexgym.co.uk" className="text-uf-red hover:underline">memberships@ultraflexgym.co.uk</a></p>
      </motion.div>
    </div>
  </section>
);

const DayPassPromo = () => (
  <section className="py-16 md:py-24 relative overflow-hidden" data-testid="day-pass-promo">
    <div className="absolute inset-0 bg-gradient-to-br from-[#e63946]/10 via-transparent to-transparent"></div>
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 bg-uf-red/10 border border-uf-red/30 rounded-full px-4 py-1.5 mb-4">
            <Ticket className="w-4 h-4 text-uf-red" />
            <span className="text-uf-red text-xs font-bold uppercase tracking-wider">Try Before You Commit</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight">
            Day Pass<br /><span className="text-uf-red">Just £10</span>
          </h2>
          <p className="text-uf-textMuted text-base md:text-lg mt-4 max-w-lg">
            Full access to any of our 11 gyms for a single day. No commitment, no contract.
          </p>
          <ul className="mt-6 space-y-3 text-left max-w-md mx-auto md:mx-0">
            {['Full gym access including all equipment', 'Valid at any UltraFlex location', 'Instant QR code for entry', 'Book online in under 2 minutes'].map(item => (
              <li key={item} className="flex items-center gap-3 text-white/80 text-sm">
                <Check className="w-4 h-4 text-uf-red flex-shrink-0" />{item}
              </li>
            ))}
          </ul>
          <Link to="/day-pass" className="inline-flex items-center gap-2 mt-8 bg-uf-red text-white font-heading font-bold uppercase tracking-wider px-8 py-4 hover:bg-uf-red/90 transition-colors" data-testid="day-pass-cta-btn">
            <Ticket className="w-5 h-5" />Buy Day Pass
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="flex-1 max-w-md">
          <div className="relative">
            <div className="absolute -inset-4 bg-uf-red/20 rounded-2xl blur-xl"></div>
            <div className="relative bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl border border-white/10 p-8 text-center">
              <Dumbbell className="w-12 h-12 text-uf-red mx-auto mb-4" />
              <p className="text-uf-red font-heading text-6xl font-black">£10</p>
              <p className="text-white font-heading text-xl mt-2">Full Day Access</p>
              <div className="mt-6 space-y-2 text-sm text-white/60">
                <p>All equipment included</p>
                <p>Any of our 11 locations</p>
                <p>QR code entry - no hassle</p>
              </div>
              <Link to="/day-pass" className="block mt-6 bg-uf-red text-white font-bold py-3 rounded-lg hover:bg-uf-red/90 transition-colors">
                Get Your Pass
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <HeroSection />
      <StatsSection />
      <LocationsShowcase />
      <VideoSection />
      <FeaturesSection />
      <CTABannersSection />
      <DayPassPromo />
      <ReviewsSection />
      <AboutSection />
      <CTASection />
    </>
  );
}
